import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import {
  FileTypeNotSupportedError,
  MaxFileSizeExceededError,
  UploadConfigurationProvider,
  UploadManagerProvider,
} from '@design-stack-vista/upload-components';
import { SubBrands, Tenants, VistaAsset } from '@design-stack-vista/vista-assets-sdk';
import { ValidationState } from '@vp/upload-and-print-validator';
import { sendEventTracking } from 'src/components/Analytics';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { useProcessUpload, useUploadContext } from 'src/hooks/UploadContextHooks';
import { useAuth } from 'src/hooks/useAuth';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import { Upload } from 'src/services/UploadAndPrintService';
import { UPLOAD_MAX_SIZE_KB } from 'src/utils/Constants';
import { addPageAction, noticeError } from 'src/utils/newRelicUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { getValidationsForProduct } from 'src/utils/validationUtil';

export const UploadProvider = ({ children }: React.PropsWithChildren): ReactElement => {
  const { vpAuth } = useAuth();
  const { setUpload, setIsUploading, setUploadRestrictionFailed } = useUploadContext();
  const { processUpload } = useProcessUpload();
  const { productInformation } = useProductContext();
  const { setValidations } = useValidationContext();
  const { trackingConfiguration } = useTrackingContext();
  const [uploadStartTime, setUploadStartTime] = useState(0);
  const { setError } = useErrorContext();

  const authProvider = useMemo(
    () => ({
      getAuthHeaders: async () => {
        return {
          Authorization: `Bearer ${vpAuth?.getToken()}`,
        };
      },
    }),
    [vpAuth]
  );

  const pdfOnlyFileTypes: NonNullable<React.ComponentProps<
    typeof UploadConfigurationProvider
  >['supportedFileTypes']> = [
    {
      fileType: 'application/pdf',
      extensions: ['pdf'],
    },
    {
      fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      extensions: ['docx'],
    },
  ];

  const fireUploadTracking = () => {
    if (trackingConfiguration && productInformation.mpvId) {
      sendEventTracking({
        ...trackingConfiguration,
        ...createTrackingDataForProduct(productInformation),
        action: 'Upload Design Clicked',
        label: `Upload`,
        category: 'Design - Upload and Print',
        variant: productInformation.selectedOptions,
      });
    }
  };

  const fireSuccessfulUploadTimeTracking = useCallback(() => {
    if (trackingConfiguration && productInformation.mpvId) {
      const uploadDuration = new Date().getTime() - uploadStartTime;
      sendEventTracking({
        ...trackingConfiguration,
        ...createTrackingDataForProduct(productInformation),
        action: 'Upload Completed',
        label: 'Upload',
        category: 'Design - Upload and Print',
        variant: productInformation.selectedOptions,
        eventDetail: JSON.stringify({ uploadDuration }),
      });
    }
  }, [trackingConfiguration, productInformation, uploadStartTime]);

  const fireFailedUploadTimeTracking = useCallback(() => {
    if (trackingConfiguration && productInformation.mpvId) {
      const uploadDuration = new Date().getTime() - uploadStartTime;
      sendEventTracking({
        ...trackingConfiguration,
        ...createTrackingDataForProduct(productInformation),
        action: 'Upload Failed',
        label: 'Upload',
        category: 'Design - Upload and Print',
        variant: productInformation.selectedOptions,
        eventDetail: JSON.stringify({ uploadDuration }),
      });
    }
  }, [productInformation, trackingConfiguration, uploadStartTime]);

  const onUpload = () => {
    fireUploadTracking();
    setUpload(null);
    setValidations(null);
    setIsUploading(true);
    setError(null);
    setUploadStartTime(new Date().getTime());
  };

  const onUploadCompleted = (asset: VistaAsset): void => {
    if (!asset) {
      return;
    }
    const newUpload: Upload = {
      fileUri: asset.getUrl(),
      uploadedFileName: asset.data?.info.storage?.fileName,
      thumbnailUrl: asset.thumbnail.getUrl(),
    };
    setUpload(newUpload);
    processUpload(newUpload);

    fireSuccessfulUploadTimeTracking();

    addPageAction('unp-upload-success', {
      fileSize: asset.data?.info.storage?.fileSizeBytes,
      product: productInformation,
    });

    setIsUploading(false);
  };

  const onUploadError = async (error: Error) => {
    const validations = await getValidationsForProduct(productInformation);
    const index = validations.findIndex(v => v.id === 'CorruptValidator');
    if (error instanceof FileTypeNotSupportedError || error instanceof MaxFileSizeExceededError) {
      setUploadRestrictionFailed(true);
      validations[index].message = 'upload.image error.message';
    }

    // TODO: get a way to know embedded fonts and password protected file
    // validations[index].message =
    //   response?.status === 500 &&
    //   UPLOAD_ERRORS_LOCALE_KEY_MAP[response.body.Message as keyof typeof UPLOAD_ERRORS_LOCALE_KEY_MAP]
    //     ? UPLOAD_ERRORS_LOCALE_KEY_MAP[response.body.Message as keyof typeof UPLOAD_ERRORS_LOCALE_KEY_MAP]
    //     : 'validations.corrupt file.message';
    validations[index].state = ValidationState.Error;
    setValidations(validations);
    setIsUploading(false);

    noticeError(error, {
      method: 'upload-error',
      error,
    });
    fireFailedUploadTimeTracking();
  };

  return (
    <UploadConfigurationProvider supportedFileTypes={pdfOnlyFileTypes} maxFileSizeInKilobytes={UPLOAD_MAX_SIZE_KB}>
      <UploadManagerProvider
        authProvider={authProvider}
        subBrand={SubBrands.VistaPrint}
        writeTenant={Tenants.VistaPrint}
        experience="upload-and-print-client"
        onUploadCompleted={onUploadCompleted}
        onUpload={onUpload}
        onError={onUploadError}
        assetsExpireOn="P1D"
      >
        {children}
      </UploadManagerProvider>
    </UploadConfigurationProvider>
  );
};
